// composition/orchestration — 取代 Unity SlotStateMachine 對局流程（見 docs/UNITY_SOURCE_ANALYSIS.md §6）。
// 頂層只保留真正影響流程分支的狀態（idle/spinning/freeGame*）；單一消除步驟內的細節
// （DestroySymbol/FillSymbol/TransformSymbol）以 presentStep() 依 Unity 時序驅動 ReelsView 原語：
//   非中獎變暗 → 浮動計分（FG 倍率>1 先演主倍率飛入）→ win loop 1.5s＋remove 爆散 → 底部贏分累加
//   → 存活符號下墜（OutBounce）→ se_cascade_{n} → 補牌（含補牌預兆）→ Wild 變身（金框）。
// 快停（forceStopRequested）：僅作用於轉輪揭示與補牌等待（對應 Unity Rotate/StopSpin/FillSymbol 的 ForceStop）。

import { StateMachine } from '../framework/statemachine/StateMachine'
import type { EventBus } from '../framework/events/EventBus'
import { logger } from '../framework/core/Logger'
import { delay } from '../framework/core/async'
import { formatInteger } from '../framework/core/format'
import { isServerError } from '../framework/net/ServerError'
import { showConfirm, showError, showErrorCode, showNotice } from './view/hud/dialogMediator'
import {
  AUTO_SPIN_INTERVAL_MS,
  BUY_BONUS_MULTIPLIER,
  BUY_TYPE_FREE_SPINS,
  FG_COLLECT_SETTLE_MS,
  FG_CUTSCENE_DELAY_MS,
  FG_MAIN_MULTIPLIER_MIN,
  FG_MULTIPLIER_INCREMENT,
  FG_SHAKE_MULTIPLIER_MIN,
  FG_SPIN_INTERVAL_MS,
  isMaxWin,
  MAIN_REEL_COUNT,
  MAX_WIN_RATIO,
  maxWinFor,
  PRE_WIN_RATIO,
  SCATTER_COLLECTOR_SLOTS,
  SCATTER_MAX_POINT,
  SCATTER_SYMBOL_ID,
  SCATTER_WOLF_MAX_LEVEL,
  scatterLevelFor,
  TRACKER_OVERLAY_REELS,
  TRACKER_REEL_INDEX,
  VISIBLE_ROWS,
  winTierFor,
  type WinTier,
} from './constants'
import { pickReelWindow } from './config/reelStrips'
import { EFFECT_ANIM } from './assets/EffectSpines'
import { injectMockTallSymbols } from './logic/tallSymbols'
import { accumulateWaysByReel, computePossibleWays } from './logic/possibleWays'
import type { GameEventMap } from './events/GameEvents'
import type { FreeGameSpinResult, SpinResponse, SpinService } from './net/SpinService'
import type { TumbleStep } from './logic/tumbleEngine'
import { cellSpan, isCovered, type Board, type CellRef, type ReelColumn } from './model/board'
import type { ReelsView } from './view/ReelsView'
import type { BackgroundView } from './view/BackgroundView'
import type { FxLayerView } from './view/FxLayerView'
import type { MultiplierView } from './view/hud/MultiplierView'
import type { CollectorView } from './view/hud/CollectorView'
import type { CharacterView } from './view/hud/CharacterView'
import type { RewardOverlayView } from './view/overlay/RewardOverlayView'

export type RoundState = 'idle' | 'spinning' | 'freeGameIntro' | 'freeGameSpinning' | 'freeGameOutro'

/** [ASSUMED] 押注選項清單，純 Mock 展示用；真實押注選項/幣別待後端 BetRange 確認。 */
const BET_OPTIONS: readonly number[] = [1, 2, 5, 10, 20, 50, 100, 200]
/** [ASSUMED] 起始餘額（貼近截圖的示意金額，非來源數值）。 */
const INITIAL_BALANCE = 1_008_095.2
/** 閒置隨機演出間隔（毫秒）：角色 30~31s、野狼 60~61s（Unity Scene 覆寫 _characterMinWait/_minWait）。 */
const CHARACTER_IDLE_MIN_MS = 30000
const WOLF_IDLE_MIN_MS = 60000

export interface GameControllerDeps {
  readonly bus: EventBus<GameEventMap>
  readonly spinService: SpinService
  readonly reels: ReelsView
  /** 場景背景（主遊戲／FG 滑動切換）；FG 進出場由本控制器 await 其轉場。 */
  readonly background: BackgroundView
  /** FG 倍率牌（顯示累積倍率、遞增彈跳、主倍率飛入）。 */
  readonly multiplier: MultiplierView
  /** Scatter 累積計量器（FG 收集槽填格）。 */
  readonly collector: CollectorView
  /** 右側角色立繪（FG 進場變身/升級/中獎分級動畫）。 */
  readonly character: CharacterView
  /** FG 進場／結算字卡覆蓋層。 */
  readonly overlay: RewardOverlayView
  /** 全螢幕特效層（收集/獎勵子彈、FG 等級過場）。 */
  readonly fx: FxLayerView
  /** retrigger 加次數子彈的飛行終點（SPIN 鈕中心，對應 Unity _freeSpinDummy）；未提供時飛向盤面中央。 */
  readonly spinTarget?: { spinButtonCenter(): { x: number; y: number } }
  /** 初始餘額（顯示單位）。真實後端提供 GetBalance 值；未提供時用 Mock 預設 INITIAL_BALANCE。 */
  readonly initialBalance?: number
  /** 押注選項清單（顯示單位）。真實後端提供 BetRange；未提供時用 Mock 預設 BET_OPTIONS。 */
  readonly betOptions?: readonly number[]
}

function randomBoard(): Board {
  const board: Board = Array.from({ length: MAIN_REEL_COUNT }, (_, reelIndex) =>
    pickReelWindow(reelIndex, VISIBLE_ROWS).map((symbol) => ({ symbol })),
  )
  // 開機/示意盤面也套用長條符號注入，讓玩法在第一次 spin 前即可見（見 logic/tallSymbols.ts）。
  return injectMockTallSymbols(board)
}

function randomTracker(): ReelColumn {
  return pickReelWindow(TRACKER_REEL_INDEX, TRACKER_OVERLAY_REELS.length).map((symbol) => ({ symbol }))
}

/** 盤面（含 Tracker 頂輪）上全部 Scatter 繪製根的位置（含跨距），依輪序排列。 */
function collectScatterRefs(board: Board, tracker: ReelColumn): { ref: CellRef; span: number }[] {
  const refs: { ref: CellRef; span: number }[] = []
  board.forEach((column, reel) => {
    column.forEach((cell, row) => {
      if (isCovered(cell) || cell.symbol !== SCATTER_SYMBOL_ID) return
      refs.push({ ref: { reel, row }, span: cellSpan(cell) })
    })
  })
  tracker.forEach((cell, position) => {
    if (cell.symbol !== SCATTER_SYMBOL_ID) return
    refs.push({ ref: { reel: TRACKER_REEL_INDEX, row: position }, span: 1 })
  })
  return refs
}

export class GameController {
  private readonly bus: EventBus<GameEventMap>
  private readonly spinService: SpinService
  private readonly reels: ReelsView
  private readonly background: BackgroundView
  private readonly multiplier: MultiplierView
  private readonly collector: CollectorView
  private readonly character: CharacterView
  private readonly overlay: RewardOverlayView
  private readonly fx: FxLayerView
  private readonly spinTarget: GameControllerDeps['spinTarget']
  private readonly fsm = new StateMachine<RoundState>((from, to) =>
    logger.debug(`round state: ${String(from)} → ${to}`),
  )
  private readonly betOptions: readonly number[]
  private balance: number
  /** 押注選項索引。Mock 預設清單起始指向 BET_OPTIONS[1]=2（對照截圖 ₱2.00）；後端清單起始 0（最小注）。 */
  private betIndex: number
  /** 自動遊戲剩餘次數（0＝未啟用）；每局結束自動續轉，直到次數用完/餘額不足/使用者停止。 */
  private autoRemaining = 0
  /** 自動遊戲無限模式（∞）：進行中但不遞減次數。 */
  private autoInfinite = false
  /** 自動遊戲「觸發免費遊戲時停止」設定（對應 Unity AutoSpinSetting.StopIfFreeGameIsActive）。 */
  private autoStopOnFreeGame = false

  constructor(deps: GameControllerDeps) {
    this.bus = deps.bus
    this.spinService = deps.spinService
    this.reels = deps.reels
    this.background = deps.background
    this.multiplier = deps.multiplier
    this.collector = deps.collector
    this.character = deps.character
    this.overlay = deps.overlay
    this.fx = deps.fx
    this.spinTarget = deps.spinTarget
    const hasServerBets = deps.betOptions !== undefined && deps.betOptions.length > 0
    this.betOptions = hasServerBets ? deps.betOptions! : BET_OPTIONS
    this.betIndex = hasServerBets ? 0 : 1
    this.balance = deps.initialBalance ?? INITIAL_BALANCE

    this.fsm.define('idle', {})
    this.fsm.define('spinning', {})
    this.fsm.define('freeGameIntro', {})
    this.fsm.define('freeGameSpinning', {})
    this.fsm.define('freeGameOutro', {})
    this.fsm.change('idle')

    this.bus.on('spinRequested', () => this.startRound(0))
    this.bus.on('buyBonusRequested', () => this.startRound(BUY_TYPE_FREE_SPINS))
    this.bus.on('betSelected', ({ index }) => this.selectBet(index))
    this.bus.on('turboToggled', ({ enabled }) => this.reels.setTurbo(enabled))
    // 快停：僅主遊戲對局進行中有效（轉輪揭示/補牌等待可略過；消除演出不可跳過，對應 Unity 三態守衛）。
    // **免費遊戲期間一律不可快停**（freeGameIntro/Spinning/Outro 皆不受理）：FG 是自動連續播放的演出，
    // 允許中途快停會與收集/升級/子彈等分段動畫互相打斷。
    this.bus.on('forceStopRequested', () => {
      if (this.fsm.state === 'spinning') this.reels.forceStop()
    })
    this.bus.on('autoSpinStarted', ({ count, stopOnFreeGame }) => {
      this.autoInfinite = !Number.isFinite(count)
      this.autoRemaining = this.autoInfinite ? 0 : count
      this.autoStopOnFreeGame = stopOnFreeGame
      this.emitAutoState()
      // 若正閒置，立即開始第一局（之後每局結束自動續轉）。
      if (this.fsm.state === 'idle') this.startAutoRoundIfPossible()
    })
    this.bus.on('autoSpinStopped', () => this.stopAuto())

    this.scheduleCharacterIdle()
    this.scheduleWolfIdle()
  }

  get currentState(): RoundState | null {
    return this.fsm.state
  }

  /** 目前押注額（BET_OPTIONS[betIndex]）。 */
  private get currentBet(): number {
    return this.betOptions[this.betIndex] ?? this.betOptions[0]!
  }

  /** 押注網格選擇：直接跳到指定索引（對應 Unity BetListWindow 點選檔位），僅閒置時可調。 */
  private selectBet(index: number): void {
    if (this.fsm.state !== 'idle') return
    if (index < 0 || index >= this.betOptions.length) return
    this.betIndex = index
    this.emitBetState()
  }

  /** 廣播當前押注額與選項清單（供底部列金額顯示、押注網格渲染/高亮、購買彈窗算花費）。 */
  private emitBetState(): void {
    this.bus.emit('betChanged', { bet: this.currentBet })
    this.bus.emit('betConfigChanged', { options: this.betOptions, index: this.betIndex })
  }

  /** 自動遊戲是否進行中（無限模式或仍有剩餘次數）。 */
  private get autoActive(): boolean {
    return this.autoInfinite || this.autoRemaining > 0
  }

  /** 廣播自動遊戲狀態（供底部列自動鈕高亮、SPIN 鈕顯示剩餘次數）。 */
  private emitAutoState(): void {
    this.bus.emit('autoSpinStateChanged', {
      active: this.autoActive,
      remaining: this.autoRemaining,
      infinite: this.autoInfinite,
    })
  }

  /** 停止自動遊戲並廣播狀態。 */
  private stopAuto(): void {
    if (!this.autoActive) return
    this.autoInfinite = false
    this.autoRemaining = 0
    this.emitAutoState()
  }

  /**
   * 餘額不足：停自動遊戲並彈共用訊息窗（對應 Unity GameEventDispatcher.IsBalanceSufficient——
   * 顯示 Error_Balance 並在自動遊戲中時一併停止；此窗按確認只關窗，不重整遊戲）。
   */
  private notifyInsufficientBalance(): void {
    logger.warn('餘額不足，無法開局')
    this.stopAuto()
    showNotice('dialog.Error_Balance')
  }

  /** 開始一個自動局：先扣一次次數（無限模式不扣），餘額不足則停止；對應 Unity MinusAutoSpinCount。 */
  private startAutoRoundIfPossible(): void {
    if (!this.autoActive) return
    if (this.balance < this.currentBet) {
      this.notifyInsufficientBalance()
      return
    }
    if (!this.autoInfinite) {
      this.autoRemaining -= 1
      this.emitAutoState()
    }
    this.startRound(0)
  }

  /** 閒置隨機演出排程：角色每 30~31s（Unity _characterMinWait/MaxWait），僅 idle 狀態觸發。 */
  private scheduleCharacterIdle(): void {
    setTimeout(() => {
      if (this.fsm.state === 'idle') this.bus.emit('characterIdle', undefined)
      this.scheduleCharacterIdle()
    }, CHARACTER_IDLE_MIN_MS + Math.random() * 1000)
  }

  /**
   * 野狼嚎叫排程：每 60~61s（Unity _minWait/_maxWait）。對應 Unity TriggerWolf 迴圈：
   * 主遊戲期間（含轉動中）都會觸發、FG 期間不觸發（StartWolfIdle 只在開場/ToMg 重啟）；
   * 音效（se_wolf）與背景野狼探頭動畫（Background_Front 播 mg_Woif_L）同時發生。
   */
  private scheduleWolfIdle(): void {
    setTimeout(() => {
      if (this.fsm.state === 'idle' || this.fsm.state === 'spinning') {
        this.bus.emit('wolfIdle', undefined)
        void this.background.playWolf()
      }
      this.scheduleWolfIdle()
    }, WOLF_IDLE_MIN_MS + Math.random() * 1000)
  }

  /** 開機時顯示的初始靜態盤面（本機隨機，不扣款、不觸發任何玩法邏輯）。 */
  showIdleBoard(): void {
    const board = randomBoard()
    const tracker = randomTracker()
    this.reels.setStatic(board, tracker)
    this.bus.emit('balanceChanged', { balance: this.balance })
    this.emitBetState()
    this.bus.emit('waysShown', { ways: computePossibleWays(board, tracker) })
  }

  /** 統一的開局進入點（一般 spin 或購買免費遊戲），兜底重置狀態避免例外卡死。 */
  private startRound(buyType: number): void {
    this.runSpin(buyType).catch((error: unknown) => {
      // runSpin() 內部一路 await 到動畫播放完，沒有任何一層有 try/catch；一旦中途拋出例外，
      // fsm 會永遠卡在 'spinning'、SPIN 鍵永久失效卻沒有任何錯誤提示。這裡兜底把狀態重置回可操作狀態。
      logger.error('開局流程發生未預期錯誤，重置狀態避免卡死', error)
      this.reels.setQuickStopActive(false)
      this.fsm.change('idle')
      this.bus.emit('spinStateChanged', { spinning: false })
      // 對照 Unity BaseGameService：後端回應錯誤碼走 Error_Code_{code}，其餘一律 Error_Unknown；
      // 兩者按下確認都會回大廳／重整頁面，故自動遊戲也一併停止。
      this.stopAuto()
      if (isServerError(error)) showErrorCode(error.code)
      else showError('dialog.Error_Unknown')
    })
  }

  /** 這一局的花費：一般 spin＝下注額；購買免費遊戲（BuyType=2）＝下注額×100。 */
  private roundCost(buyType: number): number {
    return buyType === BUY_TYPE_FREE_SPINS ? this.currentBet * BUY_BONUS_MULTIPLIER : this.currentBet
  }

  private async runSpin(buyType: number): Promise<void> {
    if (this.fsm.state !== 'idle') return
    const cost = this.roundCost(buyType)
    if (this.balance < cost) {
      this.notifyInsufficientBalance()
      return
    }

    this.fsm.change('spinning')
    this.bus.emit('spinStateChanged', { spinning: true })
    this.balance -= cost
    this.bus.emit('balanceChanged', { balance: this.balance })
    this.bus.emit('winShown', { amount: 0 })
    // 開新一轉先清空組合方式顯示為 "..."，待盤面落定後再顯示新值（對應 Unity ResetWays）。
    this.bus.emit('waysReset', undefined)
    // 開新一局（付費 spin，含購買免費遊戲）先清空右下角交易 ID，對照 Unity
    // BottomBarUIMediator.OnSpinTriggerDispatchEvent 在派發當下 OnUpdateTransText("")（結果回來才填）。
    // 免費遊戲的內部轉走 runFreeGame（不經本方法），故沿用觸發時那筆交易 ID 不被清空。
    this.bus.emit('transactionIdChanged', { txnId: '' })
    this.reels.setQuickStopActive(true)

    let response: SpinResponse
    try {
      response = await this.spinService.spin({ totalBet: this.currentBet, buyType })
    } catch (error) {
      // spin 被後端拒絕（如購買免費遊戲投注額不合法 code=13）或斷線時，還原剛才樂觀扣掉的本局花費，
      // 避免餘額顯示憑空減少（幽靈扣款）；再往上拋交給 startRound 兜底重置狀態。
      this.balance += cost
      this.bus.emit('balanceChanged', { balance: this.balance })
      throw error
    }

    // 後端本次結算交易 ID（envelope ResponseInfo.TxnId，欄位 5）→ 右下角狀態列，對照 Unity
    // BaseGameService.OnSpin 的 _setTransTextEvent.SetTransText(responseInfo.TxnId)。Mock 無此值→留空。
    this.bus.emit('transactionIdChanged', { txnId: response.txnId ?? '' })

    const firstStep = response.mainGame.steps[0]
    // steps 為空代表這次 Spin 從一開始就沒有任何中獎連鎖；finalBoard 此時就是那個未曾被消除過的初始盤面，
    // 仍必須顯示出來。有連鎖時必須改用第一步之前的 boardBefore/trackerBefore。
    const initialBoard = firstStep !== undefined ? firstStep.boardBefore : response.mainGame.finalBoard
    const initialTracker = firstStep !== undefined ? firstStep.trackerBefore : response.mainGame.trackerColumn

    // 開轉預告（僅一般 spin、這手會觸發 FG、機率 0.6；對應 Unity ShowPreWin，出現後本手不聽牌）。
    const preWin = buyType !== BUY_TYPE_FREE_SPINS && response.triggeredFreeGame && Math.random() < PRE_WIN_RATIO
    // 主遊戲：Scatter 維持一般 SS 造型（同時清除上一輪免費遊戲殘留的野狼靜態格）。
    this.reels.setFreeGame(false)
    if (preWin) {
      this.bus.emit('preWinShown', undefined)
      await this.reels.playPreWin()
    }

    // 組合方式數逐輪累乘：每一主輪由左至右停下時，即時把該輪的顆數乘上去。
    const runningWays = accumulateWaysByReel(initialBoard, initialTracker)
    await this.reels.playSpin(
      initialBoard,
      initialTracker,
      (reel) => {
        const ways = runningWays[reel]
        if (ways !== undefined) this.bus.emit('waysShown', { ways })
      },
      preWin,
    )

    let runningWin = 0
    let maxWinReached = false
    for (const [index, step] of response.mainGame.steps.entries()) {
      await this.presentStep(step, {
        freeGame: false,
        fgLevel: 1,
        firstOfSpin: index === 0,
        stepNumber: index + 1,
        onWinCommitted: () => {
          runningWin += step.win
          // Max-win 提前結算：達最高贏分上限即封頂（對應 Unity AccumulatedWin >= MaxWinValue）。
          if (isMaxWin(runningWin, this.currentBet)) {
            runningWin = maxWinFor(this.currentBet)
            maxWinReached = true
          }
          this.bus.emit('winShown', { amount: runningWin })
          this.bus.emit('waysShown', { ways: computePossibleWays(step.boardAfter, step.trackerAfter) })
        },
        abortAfterCollapse: () => maxWinReached,
      })
      if (maxWinReached) break
    }

    if (maxWinReached) {
      // 主遊戲即達最高贏分：跳過免費遊戲，直接進最高贏分慶祝結算（對應 Unity ShowMaxWin → EndGame）。
      await this.showMaxWinCelebration(runningWin)
    } else {
      // 連鎖結束（win==0）：≥10× 先播大獎慶祝（主遊戲變體；觸發 FG 也先播，對應 Unity CheckWin 順序）。
      const tier = winTierFor(runningWin, this.currentBet)
      if (tier !== 'none') await this.showWinCelebration(runningWin, this.currentBet, 1, tier)
      if (response.triggeredFreeGame && response.freeGame !== undefined) {
        await this.runFreeGame(response, runningWin, this.currentBet)
      }
    }

    // 真實後端：以伺服器權威餘額覆蓋（已反映扣款＋贏分）；Mock：本地加上總贏分。
    this.balance = response.serverBalance ?? this.balance + response.totalWin
    this.bus.emit('balanceChanged', { balance: this.balance })
    this.reels.setQuickStopActive(false)
    this.bus.emit('spinStateChanged', { spinning: false })
    this.fsm.change('idle')

    // 自動遊戲續轉：觸發免費遊戲且設「FG 時停止」則停；否則等 0.4s 開下一局（對應 Unity _autoSpinInterval，
    // 不受 Turbo 影響；次數用完/餘額不足會在 startAutoRoundIfPossible 內停止並廣播狀態）。
    if (this.autoActive) {
      if (this.autoStopOnFreeGame && response.triggeredFreeGame) {
        this.stopAuto()
      } else {
        await delay(AUTO_SPIN_INTERVAL_MS)
        if (this.fsm.state === 'idle') this.startAutoRoundIfPossible()
      }
    }
  }

  /**
   * 單一消除步驟的完整演出（對應 Unity DestroySymbol → FillSymbol → TransformSymbol）：
   *   ① 非中獎變暗＋浮動計分（FG 倍率>1：先顯示基底分 → 主倍率飛入 → 套倍後分 → ≥10 震動盤面）
   *   ② se_win/角色中獎（stepWinPresented）＋全部中獎格 win loop 1.5s → remove 爆散 → 還原變暗、收浮動計分
   *   ③ onWinCommitted()（底部贏分累加、倍率牌遞增由呼叫端處理）
   *   ④ 存活符號下墜（OutBounce）→ se_cascade_{n}（tumbleStepResolved）
   *   ⑤ abortAfterCollapse 檢查（Max-win 提前結算時跳過補牌）
   *   ⑥ 補牌（含補牌預兆）→ Wild 變身（金框）
   */
  private async presentStep(
    step: TumbleStep,
    ctx: {
      freeGame: boolean
      fgLevel: number
      firstOfSpin: boolean
      stepNumber: number
      onWinCommitted: () => void
      abortAfterCollapse?: () => boolean
    },
  ): Promise<void> {
    this.reels.maskLosers(step)
    const flyMultiplier = ctx.freeGame && step.multiplier > FG_MAIN_MULTIPLIER_MIN
    this.reels.showWinTemp(flyMultiplier ? step.win / step.multiplier : step.win)
    if (flyMultiplier) {
      // 盤面倍率>1：大倍率字從盤面中央飛入倍率牌，浮動計分再跳到套倍後金額（對應 ShowMainMultiplier）。
      this.bus.emit('freeGameMainMultiplier', { multiplier: step.multiplier })
      await this.multiplier.playMainMultiplier(step.multiplier)
      this.reels.showWinTemp(step.win)
      if (step.multiplier >= FG_SHAKE_MULTIPLIER_MIN) await this.reels.shake()
    }

    this.bus.emit('stepWinPresented', {
      win: step.win,
      firstOfSpin: ctx.firstOfSpin,
      freeGame: ctx.freeGame,
      fgLevel: ctx.fgLevel,
    })
    await this.reels.playWinAnimations(step)
    this.reels.unmaskLosers()
    this.reels.hideWinTemp()

    ctx.onWinCommitted()

    await this.reels.playCollapse(step)
    this.bus.emit('tumbleStepResolved', { step: ctx.stepNumber })
    if (ctx.abortAfterCollapse?.() === true) return

    await this.reels.playFill(step)
    await this.reels.playWildTransforms(step)
  }

  /**
   * 完整免費遊戲流程（對照 Unity ToFg → Rotate…CheckWin 迴圈 → EndGame）：
   *   進場：Scatter 中獎展示 → 進場字卡（BGM mu_cutscene）→ 0.4s → 背景滑入 FG → 倍率牌/收集面板進場
   *         （se_fgui）→ 角色變身登場 → Lv1 過場（se_pk_1）。
   *   進行：每轉間隔 0.2s；逐輪轉動＋消除連鎖（倍率牌每步 +1、主倍率飛入、≥10 震動）；單轉 ≥10× 播
   *         FG 變體大獎慶祝；轉末收集 Scatter（子彈→計量器→升級/發獎→retrigger 子彈飛 SPIN 鈕）。
   *   結算：（達 Max-win 先播最高贏分字卡）背景滑回 → BGM 回主遊戲 → FG 結算字卡（金額直接顯示）。
   * @param baseWin 觸發 FG 的那次主遊戲已累積贏分（結算總額＝baseWin＋FG 贏分）。
   * @param totalBet 玩家下注（大獎分級門檻的分母，對應 Unity PlayerBetData.TotalBet）。
   */
  private async runFreeGame(response: SpinResponse, baseWin: number, totalBet: number): Promise<void> {
    const spins = response.freeGame!.spins
    // 顯示總轉數從「基礎次數」起算（retrigger 加的次數在收集當下才揭露成長，不一開始就爆雷）。
    const addedSpins = spins.reduce(
      (sum, spin) => sum + spin.scatterEvents.reduce((s, e) => s + (e.bonus?.addSpinCount ?? 0), 0),
      0,
    )
    let revealedTotal = spins.length - addedSpins

    // ── 進場（ToFg） ──
    this.fsm.change('freeGameIntro')
    // 免費遊戲不提供快停：關掉盤面熱區（連帶取消 pointer 游標），行為面另有 forceStopRequested 的狀態守衛。
    this.reels.setQuickStopActive(false)
    // Scatter 中獎展示：非 Scatter 變暗、Scatter 播 win loop（se_SS_win）。
    const scatterRefs = collectScatterRefs(response.mainGame.finalBoard, response.mainGame.trackerColumn)
    if (scatterRefs.length > 0) {
      this.bus.emit('scatterShowcaseShown', undefined)
      await this.reels.playScatterShowcase(scatterRefs.map((s) => s.ref))
    }
    this.bus.emit('freeGameStarted', { spinCount: revealedTotal })
    // 進場字卡期間切過場 BGM（mu_cutscene），關閉後回 FG BGM（對應 ResumeBGM mu_free_background_1）。
    this.bus.emit('bgmChanged', { track: 'cutscene' })
    await this.overlay.showFreeSpins(revealedTotal)
    this.bus.emit('bgmChanged', { track: 'free' })
    await delay(FG_CUTSCENE_DELAY_MS)
    // 背景滑入 FG（se_shift）→ 倍率牌 X1／收集面板進場（se_fgui）→ 盤面金光（ani_reel_glow，非阻塞）
    // → 角色變身登場 → Lv1 過場（對照 Unity ToFg 順序：collect 進場 → reelGlow → PlayCharacterUpgrade(0)）。
    this.bus.emit('sceneShift', undefined)
    await this.background.transitionToFreeGame()
    this.bus.emit('fgPanelsShown', undefined)
    this.multiplier.show()
    this.collector.beginFreeGame()
    void this.fx.playReelGlow()
    this.bus.emit('scatterLevelUp', { level: 1 })
    await this.character.enterFreeGame()
    this.bus.emit('fgLevelIntroShown', { level: 1 })
    await this.fx.playLevelTrans(1)

    // ── 進行 ──
    this.fsm.change('freeGameSpinning')
    let freeGameWin = 0
    let maxWinReached = false
    // FG 中 Scatter 改用野狼分級圖：等級依「本轉開始前」已累積的計量點數（對照 Unity Trans2BoardData 用
    // GetLevel(CurrentScatterCount)）。
    let collectedScatter = 0
    for (const [spinIndex, spin] of spins.entries()) {
      if (spinIndex > 0) await delay(FG_SPIN_INTERVAL_MS)
      const fgLevel = scatterLevelFor(collectedScatter)
      // 剩餘次數於開轉時遞減（對應 Unity Rotate_Enter 的 FreeSpinsCompleted++ → UpdateFreeGameCount）。
      this.bus.emit('freeGameProgress', { current: spinIndex + 1, total: revealedTotal })
      // 每輪開始用權威起始倍率同步倍率牌（不帶動畫），避免逐步遞增與後端 CumMultiplier 累積漂移。
      this.multiplier.setMultiplier(spin.startMultiplier, false)
      this.bus.emit('freeGameMultiplierChanged', { multiplier: spin.startMultiplier, animate: false })
      this.bus.emit('waysReset', undefined)

      const firstStep = spin.steps[0]
      const fgBoard = firstStep !== undefined ? firstStep.boardBefore : spin.finalBoard
      const fgTracker = firstStep !== undefined ? firstStep.trackerBefore : spin.trackerColumn
      const fgRunningWays = accumulateWaysByReel(fgBoard, fgTracker)
      // 本轉 Scatter 野狼等級（野狼圖只有 3 級，第 4 級沿用 S3）。FG 亦關閉聽牌/預兆（ReelsView 內守衛）。
      this.reels.setFreeGame(true, Math.min(SCATTER_WOLF_MAX_LEVEL, fgLevel))
      await this.reels.playSpin(fgBoard, fgTracker, (reel) => {
        const ways = fgRunningWays[reel]
        if (ways !== undefined) this.bus.emit('waysShown', { ways })
      })

      for (const [index, step] of spin.steps.entries()) {
        await this.presentStep(step, {
          freeGame: true,
          fgLevel,
          firstOfSpin: index === 0,
          stepNumber: index + 1,
          onWinCommitted: () => {
            freeGameWin += step.win
            // Max-win 提前結算：達最高贏分上限即封頂並停止後續連鎖與剩餘免費局。
            if (isMaxWin(baseWin + freeGameWin, totalBet)) {
              freeGameWin = maxWinFor(totalBet) - baseWin
              maxWinReached = true
            }
            this.bus.emit('winShown', { amount: baseWin + freeGameWin })
            this.bus.emit('waysShown', { ways: computePossibleWays(step.boardAfter, step.trackerAfter) })
            // 每次有贏分消除後倍率累加（跨旋轉保留）。對應 Unity DestroySymbol 的 CurrentMultiplier++。
            const nextMultiplier = step.multiplier + FG_MULTIPLIER_INCREMENT
            this.multiplier.setMultiplier(nextMultiplier, true)
            this.bus.emit('freeGameMultiplierChanged', { multiplier: nextMultiplier, animate: true })
          },
          abortAfterCollapse: () => maxWinReached,
        })
        if (maxWinReached) break
      }
      if (maxWinReached) break

      // 單轉大獎慶祝（FG 變體 2；對應 Unity FG 的 CheckWin 於連鎖結束後 ShowWinCelebration）。
      const spinTier = winTierFor(spin.totalWin, totalBet)
      if (spinTier !== 'none') await this.showWinCelebration(spin.totalWin, totalBet, 2, spinTier)

      // ── Scatter 累積計量：本轉落地的 Scatter 逐顆收集 ──
      revealedTotal = await this.processScatterEvents(spin, spinIndex, revealedTotal)
      const lastEvent = spin.scatterEvents[spin.scatterEvents.length - 1]
      if (lastEvent !== undefined) collectedScatter = lastEvent.count
    }

    // ── 結算 ──
    const totalWin = baseWin + freeGameWin
    // 達最高贏分：先播最高贏分字卡（對應 Unity DestroySymbol 內 ShowMaxWin 先於 EndGame 轉場）。
    if (maxWinReached) {
      await this.showMaxWinCelebration(totalWin)
    }
    this.fsm.change('freeGameOutro')
    // 離開 FG：Scatter 還原成一般 SS 造型、恢復主遊戲聽牌（重繪最後一輪 FG 盤面的野狼靜態格）。
    this.reels.setFreeGame(false)
    this.collector.endFreeGame()
    this.multiplier.hide()
    this.character.exitFreeGame()
    this.bus.emit('sceneShift', undefined)
    await this.background.transitionToMainGame()
    this.bus.emit('bgmChanged', { track: 'main' })
    this.bus.emit('winShown', { amount: totalWin })

    if (!maxWinReached) {
      const tier = winTierFor(totalWin, totalBet)
      // FG 結算字卡（totalWin1~4 依整輪累積 Scatter 數；金額直接顯示）；期間切 mu_congrats，關閉後回主遊戲。
      this.bus.emit('freeGameSettleShown', { totalWin, tier })
      await this.overlay.showFreeGameSettle(totalWin, collectedScatter, tier)
      this.bus.emit('bgmChanged', { track: 'main' })
    }
    this.bus.emit('freeGameEnded', { totalWin: freeGameWin })
  }

  /** 大獎慶祝（逐級跑分；variant 1=主遊戲、2=免費遊戲）。報獎音效由 celebration* 事件驅動。 */
  private async showWinCelebration(totalWin: number, totalBet: number, variant: 1 | 2, tier: WinTier): Promise<void> {
    this.bus.emit('winCelebrationShown', { totalWin, tier })
    await this.overlay.showWinCelebration(totalWin, totalBet, variant, tier)
  }

  /**
   * 最高贏分慶祝；對應 Unity RewardPresentationPanelMediator.ShowMaxWin：
   * **先**彈共用訊息窗（System_MaxWin，{0}＝最高倍數）並等玩家按下確認鈕，**才**播 ani_maxWin 字卡。
   */
  private async showMaxWinCelebration(totalWin: number): Promise<void> {
    await showConfirm('dialog.System_MaxWin', [formatInteger(MAX_WIN_RATIO)])
    this.bus.emit('winCelebrationShown', { totalWin, tier: 'max' })
    await this.overlay.showMaxWin(totalWin)
  }

  /**
   * 處理一次免費旋轉落地的 Scatter 計量事件（依序播放，對應 Unity CheckWin 的 FG 收集迴圈）：
   *   每顆 → 盤面 Scatter 播 win loop（se_wolf_win）→ 子彈飛入計量器（se_collect）→ 爪槽點亮；
   *   發獎 → 加倍率（獎勵面板子彈→倍率牌）／加免費次數（子彈→SPIN 鈕，retrigger 揭露總轉數成長）；
   *   升級 → 角色升級動畫＋等級過場（se_pk_{n}）＋BGM 分級（scatterLevelUp）。
   * @returns 更新後的顯示總轉數（含本轉 retrigger 加成）。
   */
  private async processScatterEvents(
    spin: FreeGameSpinResult,
    spinIndex: number,
    revealedTotal: number,
  ): Promise<number> {
    let total = revealedTotal
    if (spin.scatterEvents.length === 0) return total

    // 本轉最終盤面上的 Scatter 位置佇列（依輪序），供收集展示與子彈起點；事件多於盤面 Scatter（Mock
    // 注入）時以盤面中央代替。
    const refQueue = collectScatterRefs(spin.finalBoard, spin.trackerColumn)

    for (const event of spin.scatterEvents) {
      const located = refQueue.shift()
      const slot = Math.min(event.count, SCATTER_COLLECTOR_SLOTS) - 1
      // 計量條在收集這顆「之前」是否已滿：滿格後不再演出「子彈飛計量器＋爪槽」，
      // 只保留 Scatter 中獎展示 → 子彈直飛 SPIN 鈕加次數（retrigger）。
      const meterAlreadyFull = event.count > SCATTER_MAX_POINT

      // 盤面 Scatter 中獎展示（狼嚎）→（未滿格）子彈飛入計量器 → 爪槽點亮。
      this.bus.emit('scatterHighlightShown', undefined)
      if (located !== undefined) await this.reels.playScatterCollectHighlight(located.ref)
      const from =
        located !== undefined
          ? this.reels.cellCenter(located.ref.reel, located.ref.row, located.span)
          : this.reels.boardCenter()
      if (!meterAlreadyFull) {
        this.bus.emit('scatterCollected', { count: event.count, level: event.level, slot })
        await this.fx.flyBullet(from, this.collector.slotCenter(slot))
        await this.collector.fillSlot(event.count, event.level)
      }

      // 里程碑發獎（對應 Unity 先發獎、後升級演出的順序）。
      const bonus = event.bonus
      if (bonus !== undefined) {
        this.bus.emit('scatterBonusAwarded', { addMultiplier: bonus.addMultiplier, addSpinCount: bonus.addSpinCount })
        if (bonus.addMultiplier > 0) {
          // 加倍率子彈：獎勵面板（Lv2→X3、Lv3→X5）→ 倍率牌，落定後倍率牌跳到本轉結束倍率。
          const panelIndex = event.level >= 3 ? 1 : 0
          await this.fx.flyBullet(this.collector.rewardItemCenter(panelIndex), this.multiplier.plaqueCenter())
          this.multiplier.setMultiplier(spin.endMultiplier, true)
          this.bus.emit('freeGameMultiplierChanged', { multiplier: spin.endMultiplier, animate: true })
        }
        if (bonus.addSpinCount > 0) {
          // 加次數子彈：計量未滿由獎勵面板（Lv3→+3、Lv4→+8）射出；滿格 retrigger 由盤面 Scatter 直飛。
          const fromSpins = meterAlreadyFull ? from : this.collector.rewardItemCenter(event.level >= 4 ? 3 : 2)
          const target = this.spinTarget?.spinButtonCenter() ?? this.reels.boardCenter()
          await this.fx.flyBullet(fromSpins, target)
          // 子彈落定 → SPIN 鈕上播 ani_freespins「+N」爆點（對應 SpawnFreeSpinBullet 尾段 _freeSpinSpin），
          // 同時揭露總轉數成長（retrigger，對應 UpdateFreeGameCount）。爆點非阻塞，不拖慢收集節奏。
          void this.fx.playBurst('freespins', EFFECT_ANIM.freeSpinBurst, target)
          total += bonus.addSpinCount
          this.bus.emit('freeGameProgress', { current: spinIndex + 1, total })
        }
      }

      if (event.leveledUp) {
        this.bus.emit('scatterLevelUp', { level: event.level })
        await this.character.playUpgrade(event.level)
        this.bus.emit('fgLevelIntroShown', { level: event.level })
        await this.fx.playLevelTrans(event.level)
      }
    }

    // 收集全部處理完的收尾延遲（對應 Unity _updateFreeSpinCountDelay=0.8）。
    await delay(FG_COLLECT_SETTLE_MS)
    return total
  }
}
